import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, BoltIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductDisambiguation from '../components/ProductDisambiguation';
import { 
  api, 
  Product, 
  ProductDisambiguationResponse,
  ProductAnalysisResponse 
} from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { API_ENDPOINTS } from '../config/environment';

interface SystemStats {
  totals: {
    products: number;
    components: number;
    processes: number;
    jobs: number;
  };
  analysis: {
    completed_products: number;
    analyzing_products: number;
    completion_rate: number;
  };
  activity: {
    recent_actions_24h: number;
  };
  updated_at: string;
}

const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [recentlyAnalyzed, setRecentlyAnalyzed] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchSuggestions, setSearchSuggestions] = useState<Product[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [disambiguation, setDisambiguation] = useState<ProductDisambiguationResponse | null>(null);
  const [originalSearchQuery, setOriginalSearchQuery] = useState('');
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const navigate = useNavigate();

  // Load recently analyzed products and system stats
  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load recent products and system stats in parallel
      const [productsResponse, statsResponse] = await Promise.all([
        api.getProducts({
          analysis_status: 'completed',
          limit: 6,
          category: selectedCategory || undefined
        }),
        fetch(API_ENDPOINTS.public.stats)
          .then(res => res.ok ? res.json() : null)
          .catch(() => null)
      ]);
      
      setRecentlyAnalyzed(productsResponse);
      if (statsResponse && statsResponse.totals) {
        setSystemStats(statsResponse);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setRecentlyAnalyzed([]);
      toast.error('Failed to load recent products');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount and when category changes
  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  // Handle search input changes for auto-complete
  useEffect(() => {
    const searchForSuggestions = async () => {
      if (searchQuery.trim().length >= 2) {
        try {
          const suggestions = await api.getProducts({
            search: searchQuery,
            limit: 5
          });
          setSearchSuggestions(suggestions);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Failed to load search suggestions:', error);
          setSearchSuggestions([]);
        }
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(searchForSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    setIsAnalyzing(true);
    setOriginalSearchQuery(searchQuery);
    
    try {
      // First try to search for existing products
      const searchResults = await api.searchProducts(searchQuery, 5);
      
      if (searchResults.length > 0) {
        // Navigate to search results if products found
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        return;
      }

      // Start new product analysis with disambiguation
      const result = await api.analyzeProduct({
        product_name: searchQuery
      });
      
      // Check if we need disambiguation
      if ('needs_disambiguation' in result && result.needs_disambiguation) {
        // Show disambiguation UI
        setDisambiguation(result as ProductDisambiguationResponse);
      } else {
        // Direct analysis result
        const analysisResult = result as ProductAnalysisResponse;
        if (analysisResult.existing) {
          toast.success('Product found! Redirecting to analysis...');
          navigate(`/products/${analysisResult.slug}`);
        } else {
          toast.success('Analysis started! Redirecting to product page...');
          navigate(`/products/${analysisResult.slug}`);
        }
      }
    } catch (error: any) {
      console.error('Search/analysis failed:', error);
      
      // Handle rate limiting errors specially
      if (error.isRateLimit) {
        const rateLimitData = error.rateLimitData;
        if (rateLimitData?.user_class === 'anonymous') {
          toast.error(
            'Rate limit reached! Anonymous users are limited to 5 analyses per hour. Please sign in for higher limits.',
            { duration: 6000 }
          );
        } else {
          toast.error(error.message, { duration: 6000 });
        }
      } else {
        toast.error('Failed to analyze product. Please try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSuggestionClick = (product: Product) => {
    setSearchQuery('');
    setShowSuggestions(false);
    navigate(`/products/${product.slug}`);
  };

  const handleCancelDisambiguation = () => {
    setDisambiguation(null);
    setSearchQuery(originalSearchQuery);
  };

  const handleCategoryClick = (categoryName: string) => {
    const categoryKey = categoryName.toLowerCase();
    if (selectedCategory === categoryKey) {
      // Deselect if already selected
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryKey);
    }
  };

  const categories = [
    { emoji: '📱', name: 'Electronics', key: 'electronics' },
    { emoji: '🚗', name: 'Automotive', key: 'automotive' },
    { emoji: '👕', name: 'Apparel', key: 'apparel' },
    { emoji: '☕', name: 'Food', key: 'food' },
  ];

  // Show disambiguation if needed
  if (disambiguation) {
    return (
      <>
        <title>ProductTrace - Product Disambiguation</title>
        <meta 
          name="description" 
          content="Select the specific product you want to analyze." 
        />

        <div className="min-h-screen bg-gray-50">
          <Header />
          <ProductDisambiguation 
            disambiguation={disambiguation}
            originalContext={originalSearchQuery}
            onCancel={handleCancelDisambiguation}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <title>ProductTrace - How Things Are Made</title>
      <meta 
        name="description" 
        content="Explore products, components, and manufacturing processes. Discover how things are made with AI-powered analysis." 
      />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="mb-6">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                🏭 How Things Are Made
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">
                Explore products, components, and manufacturing processes
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600 max-w-3xl mx-auto">
                <div className="flex items-center bg-green-50 px-3 py-1 rounded-full">
                  <span className="mr-1">🌱</span>
                  Environmental Impact
                </div>
                <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                  <span className="mr-1">👥</span>
                  Labor Conditions
                </div>
                <div className="flex items-center bg-purple-50 px-3 py-1 rounded-full">
                  <span className="mr-1">🌍</span>
                  Country of Origin
                </div>
                <div className="flex items-center bg-orange-50 px-3 py-1 rounded-full">
                  <span className="mr-1">🎯</span>
                  Design Rationale
                </div>
              </div>
            </div>

            {/* Search Box */}
            <form onSubmit={handleSearch} className="mb-8">
              <div className="relative max-w-2xl mx-auto">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder='Search for any product... "iPhone 15", "Tesla Model Y", "Nike Air Force 1"'
                  className="block w-full pl-12 pr-24 py-4 text-base border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  disabled={isAnalyzing}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <button
                    type="submit"
                    disabled={isAnalyzing}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isAnalyzing ? (
                      <>
                        <div className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                        Analyzing
                      </>
                    ) : (
                      <>
                        Analyze
                        <BoltIcon className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </button>
                </div>

                {/* Search Suggestions Dropdown */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    {searchSuggestions.map((product) => (
                      <div
                        key={product.id}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => handleSuggestionClick(product)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{product.display_name}</div>
                            <div className="text-sm text-gray-500">
                              {product.analysis_status === 'completed' ? (
                                <span className="text-green-600">✅ Analyzed - {product.analyzed_components} components</span>
                              ) : (
                                <span className="text-yellow-600">⏳ {product.analysis_progress}% complete</span>
                              )}
                            </div>
                          </div>
                          <div className="text-blue-600">→</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>

            {/* Category Pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => handleCategoryClick(category.key)}
                  className={`px-4 py-2 border rounded-full text-sm font-medium transition-colors shadow-sm ${
                    selectedCategory === category.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  {category.emoji} {category.name}
                </button>
              ))}
              {selectedCategory && (
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Clear filter ✕
                </button>
              )}
            </div>
          </div>

          {/* Recently Analyzed Section */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              🔥 {selectedCategory ? `${categories.find(c => c.key === selectedCategory)?.name} Products:` : 'Recently Analyzed:'}
            </h2>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                <span className="ml-3 text-gray-600">Loading products...</span>
              </div>
            ) : recentlyAnalyzed.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recentlyAnalyzed.map((product) => {
                  // Get real environmental and labor data from the product
                  const envImpact = product.overall_environmental_impact;
                  const primaryCountry = product.primary_production_countries?.[0];
                  
                  return (
                    <div
                      key={product.id}
                      className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer group"
                      onClick={() => navigate(`/products/${product.slug}`)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-lg mr-3">•</span>
                          <div>
                            <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                              {product.display_name}
                            </span>
                            <div className="text-sm text-gray-500">
                              {product.analyzed_components} components analyzed
                            </div>
                            {product.description && (
                              <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                                {product.description.length > 80 
                                  ? `${product.description.substring(0, 80)}...` 
                                  : product.description}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          →
                        </div>
                      </div>
                      
                      {/* Impact Summary */}
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div className="flex items-center">
                          <span className="mr-1">🌱</span>
                          {envImpact?.carbon_footprint_kg ? `${envImpact.carbon_footprint_kg}kg CO₂` : 'N/A'}
                        </div>
                        <div className="flex items-center">
                          <span className="mr-1">🌍</span>
                          {primaryCountry ? `${primaryCountry.country} ${primaryCountry.percentage}%` : 'N/A'}
                        </div>
                        <div className="flex items-center">
                          <span className="mr-1">👥</span>
                          {product.total_worker_count ? `${product.total_worker_count.toLocaleString()} workers` : 'N/A'}
                        </div>
                        <div className="flex items-center">
                          <span className="mr-1">💰</span>
                          {formatCurrency(product.manufacturing_cost)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">📦</div>
                <p className="text-gray-600">
                  {selectedCategory 
                    ? `No ${categories.find(c => c.key === selectedCategory)?.name.toLowerCase()} products found.`
                    : 'No products analyzed yet.'
                  }
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Try searching for a product above to get started!
                </p>
              </div>
            )}
          </div>

          {/* Database Status */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📊 Database Status:
            </h3>
            {systemStats?.totals ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {systemStats.totals.components?.toLocaleString() || '0'}
                  </div>
                  <div className="text-sm text-gray-600">components</div>
                </div>
                <div className="border-l border-gray-200 md:border-l-0 md:border-r">
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {systemStats.totals.processes?.toLocaleString() || '0'}
                  </div>
                  <div className="text-sm text-gray-600">processes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {systemStats.totals.products?.toLocaleString() || '0'}
                  </div>
                  <div className="text-sm text-gray-600">products</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-gray-400 mb-1">---</div>
                  <div className="text-sm text-gray-600">components</div>
                </div>
                <div className="border-l border-gray-200 md:border-l-0 md:border-r">
                  <div className="text-2xl font-bold text-gray-400 mb-1">---</div>
                  <div className="text-sm text-gray-600">processes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-400 mb-1">---</div>
                  <div className="text-sm text-gray-600">products</div>
                </div>
              </div>
            )}
            
            {systemStats?.analysis?.completion_rate !== undefined && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Analysis completion rate: {systemStats.analysis.completion_rate || 0}%</span>
                  <span>Last updated: {systemStats.updated_at ? new Date(systemStats.updated_at).toLocaleTimeString() : 'Unknown'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <Footer />
      </div>
    </>
  );
};

export default HomePage; 